import React from "react";
import styles from "../styles";
import { Helmet } from "react-helmet";

const TOS = () => {
  return (
    <div className={`${styles.paragraph} container mx-auto p-4 px-10 max-w-4xl`}>
      <Helmet>
        <title>Terms of Service | Tech Tails</title>
        <meta
          name="description"
          content="Terms of Service and Tutoring Agreement for Tech Tails — policies on attendance, no-shows, cancellations, payments, and more."
        />
        <meta
          name="keywords"
          content="Tech Tails Terms of Service, tutoring agreement, no-show policy, cancellation policy, online learning terms"
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <h1 className="text-3xl font-bold mb-1">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-8">Last Updated: February 2025</p>

      {/* ── 1. OVERVIEW ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">1. Overview</h2>
        <p className="mb-3">
          This Terms of Service ("Agreement") is a binding contract between
          you, the parent or legal guardian ("you"), and Tech Tails, LLC ("Tech Tails," "we," "us").
          It covers every service we offer — group classes, one-on-one tutoring sessions, camps,
          workshops, and anything else provided through{" "}
          <a href="/" className="text-pinkprimary hover:text-orangeprimary font-medium">
            www.techtails.com
          </a>{" "}
          (collectively, "Services").
        </p>
        <p>
          By enrolling a student ("Enrollee") or using our platform, you agree to every term below
          on your own behalf and on behalf of your Enrollee.
        </p>
      </section>

      {/* ── 2. SERVICES ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">2. What We Offer</h2>
        <p className="mb-3">
          Tech Tails provides online coding education for children, including game design, robotics,
          art, and general programming. We offer group classes and private tutoring, all taught in
          English unless otherwise noted.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Availability of specific courses or tutoring slots is not guaranteed.</li>
          <li>Only registered Enrollees may participate — one Enrollee per account per session.</li>
          <li>We reserve the right to refuse or end enrollment for any reason, including past
            disruptive behavior or nonpayment.</li>
          <li>Sessions may be recorded for quality and safety purposes. Recordings are never shared
            except with law enforcement when student safety is at risk.</li>
        </ul>
      </section>

      {/* ── 3. PAYMENT ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">3. Payment & Fees</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong>Scheduled time slots are not guaranteed until the corresponding invoice is
            paid.</strong> We do our best to hold requested times, but availability is first-come,
            first-served and a slot is only confirmed once payment is received.</li>
          <li>Payment is due at enrollment or per the schedule shown at checkout.</li>
          <li>All fees are <strong>non-refundable</strong> after the first session begins, unless we
            decide otherwise at our discretion.</li>
          <li>If you choose a recurring payment plan, you authorize us to charge the card on file on
            each due date.</li>
          <li>Late payments: you have <strong>5 business days</strong> to catch up. After that, the
            Enrollee is paused until the balance is current, and a late fee may apply.</li>
          <li>Promotional discounts are limited to one per Enrollee, must be applied at registration,
            and cannot be combined or applied retroactively.</li>
        </ul>
      </section>

      {/* ── 4. ATTENDANCE ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">4. Attendance & Punctuality</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Sessions start at the scheduled time — it is the parent/guardian's responsibility to
            make sure the Enrollee is present and ready.</li>
          <li>Time lost due to late arrival may not be made up or extended unless otherwise agreed
            upon by the instructor. We encourage families to reach out if running late so we can
            do our best to accommodate.</li>
          <li>Occasionally, an instructor may start a session a few minutes late due to scheduling
            or technical reasons. When this happens, the instructor will make up the lost time
            during the session or arrange a make-up at no extra charge.</li>
          <li>If an Enrollee leaves early, remaining time is forfeited unless prior arrangements
            have been made.</li>
          <li>The Enrollee's computer should be set up with all required software before the
            session to make the most of their time. If technical issues come up, instructors are
            happy to help, but extended troubleshooting may count toward session time.</li>
        </ul>
      </section>

      {/* ── 5. NO-SHOW POLICY ── */}
      <section className="mb-10 bg-red-50 border border-red-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3 text-red-800">5. No-Show Policy</h2>
        <p className="mb-3">
          This policy applies to <strong>every</strong> Tech Tails service — group classes,
          one-on-one tutoring, camps, and workshops.
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>What counts as a no-show:</strong> The Enrollee does not appear within
            15 minutes of the scheduled start and no prior notice was given.
          </li>
          <li>
            <strong>No refunds or credits.</strong> A missed session due to a no-show is charged in
            full and will not be rescheduled or made up.
          </li>
          <li>
            <strong>Instructor wait time:</strong> The instructor will wait up to 15 minutes. After
            that the session ends and is recorded as a no-show.
          </li>
          <li>
            <strong>Group classes:</strong> Group classes run as scheduled regardless of individual
            attendance. Missing one is treated as a no-show under the same terms.
          </li>
        </ul>
      </section>

      {/* ── 6. CANCELLATION & RESCHEDULING ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">6. Cancellation & Rescheduling</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>24-hour notice required.</strong> Cancel or reschedule at least 24 hours before
            the session to avoid being charged. Rescheduling is subject to instructor availability.
          </li>
          <li>
            <strong>Less than 24 hours = no-show.</strong> Late cancellations are treated and
            charged as a no-show.
          </li>
          <li>
            <strong>How to cancel:</strong> Email{" "}
            <a href="mailto:teacher@techtails.com" className="text-pinkprimary hover:text-orangeprimary font-medium">
              teacher@techtails.com
            </a>
            , text{" "}
            <a href="tel:8017047878" className="text-pinkprimary hover:text-orangeprimary font-medium">
              (801) 704-7878
            </a>
            , or use the platform.
          </li>
          <li>
            <strong>If we cancel:</strong> We will reschedule at a time that works for you at no
            extra cost, or apply a credit to your account.
          </li>
          <li>
            <strong>Recurring enrollments:</strong> Cancelling a recurring schedule requires
            written notice. Individual sessions within a recurring plan still follow the 24-hour
            rule.
          </li>
        </ul>
      </section>

      {/* ── 7. EQUIPMENT ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">7. Equipment</h2>
        <p>
          Enrollees need their own computer and a stable internet connection for online sessions.
          You are responsible for meeting our minimum system requirements. We do not guarantee
          compatibility with all hardware or networks. In-person participants will be provided a
          workstation.
        </p>
      </section>

      {/* ── 8. CODE OF CONDUCT ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">8. Code of Conduct</h2>
        <p className="mb-3">
          We expect respectful behavior from every Enrollee and parent. Violating this code — or
          acting in a way that disrupts the learning environment — can result in immediate removal
          without warning and without refund. This includes:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Disrespectful, disruptive, or harmful behavior toward staff or other students.</li>
          <li>Failure to follow instructor directions.</li>
          <li>Using Tech Tails accounts or tools for unauthorized purposes.</li>
          <li>Insufficient English language skills to participate meaningfully (courses require a
            high level of English comprehension).</li>
        </ul>
      </section>

      {/* ── 9. SPECIAL ACCOMMODATIONS ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">9. Special Accommodations</h2>
        <p>
          We believe every child should have the chance to learn. If your Enrollee needs a special
          accommodation, contact us at{" "}
          <a href="mailto:teacher@techtails.com" className="text-pinkprimary hover:text-orangeprimary font-medium">
            teacher@techtails.com
          </a>{" "}
          at least two weeks before the first session. We will do our best, but cannot guarantee
          every request can be met.
        </p>
      </section>

      {/* ── 10. INTELLECTUAL PROPERTY ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">10. Intellectual Property & Ownership</h2>
        <p>
          All Tech Tails content — courses, curriculum, tutoring materials, the website, and
          related assets — are the exclusive property of Tech Tails. No rights are transferred to
          you or the Enrollee. You agree to respect all copyright and trademark protections of Tech
          Tails, our partners, and any third-party tools used in our courses.
        </p>
      </section>

      {/* ── 11. MEDIA RELEASE ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">11. Media & Likeness Release</h2>
        <p>
          By enrolling, you authorize Tech Tails and its partners to photograph, record, and use
          the name, likeness, testimonials, and project work of you and your Enrollee in
          promotional materials, online media, and press. All such content is the property of Tech
          Tails, with a royalty-free, perpetual license. You will not be compensated for this use
          and you release Tech Tails from liability related to it.
        </p>
      </section>

      {/* ── 12. SOFTWARE ACCOUNTS ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">12. Software Accounts</h2>
        <p>
          Some activities require online accounts. You consent to Tech Tails creating accounts on
          behalf of your Enrollee as needed. If an Enrollee creates an account without instructor
          permission, you release Tech Tails from all responsibility related to that account.
        </p>
      </section>

      {/* ── 13. GAMING RATINGS ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">13. Gaming Content & Ratings</h2>
        <p>
          While we minimize exposure to age-inappropriate content, we cannot guarantee that younger
          Enrollees will never encounter games rated "T" (Teen) or "M" (Mature). Students in 13+
          courses may be exposed to M-rated content. You voluntarily assume all associated risks.
        </p>
      </section>

      {/* ── 14. LIABILITY ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">14. Liability & Waiver</h2>
        <p className="uppercase text-sm leading-relaxed">
          On behalf of your child, you release Tech Tails, LLC, its owners, instructors, agents,
          partners, facility providers, and employees from liability — including claims based on
          negligence — for any damages, injuries, or property damage arising from participation in
          Tech Tails programs. You agree to be fully responsible for any such damages or injuries.
          This release does not cover gross negligence, reckless conduct, or intentional wrongdoing.
          If any part of this section is found unenforceable, the remainder stays in effect.
        </p>
      </section>

      {/* ── 15. INDEMNIFICATION ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">15. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Tech Tails, its officers, directors,
          employees, and agents from any claims, damages, losses, or expenses — including
          reasonable attorneys' fees — arising from your Enrollee's participation in any Tech
          Tails service.
        </p>
      </section>

      {/* ── 16. ARBITRATION ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">16. Arbitration</h2>
        <p>
          Any dispute (other than collections) arising from this Agreement or your Enrollee's
          participation will be resolved by binding arbitration under the Commercial Rules of the
          American Arbitration Association. The prevailing party may recover costs and reasonable
          attorney's fees. By enrolling, you acknowledge and accept this arbitration clause.
        </p>
      </section>

      {/* ── 17. CHANGES ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">17. Changes to This Agreement</h2>
        <p>
          Tech Tails may update this Agreement at any time. Continued use of our Services after
          changes are posted constitutes acceptance of the updated terms. We recommend reviewing
          this page periodically.
        </p>
      </section>

      {/* ── 18. SMS ── */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold mb-3">18. SMS Terms</h2>
        <p className="mb-3">
          If you opt in to SMS messaging through our contact form:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Reply <strong>STOP</strong> at any time to unsubscribe.</li>
          <li>Reply <strong>HELP</strong> and our team will reach out.</li>
          <li>Message and data rates may apply. We deliver to all major carriers.</li>
          <li>By opting in, you authorize Tech Tails to send recurring texts using automated
            technology.</li>
          <li>SMS consent is not required to use our Services.</li>
          <li>Questions? Email{" "}
            <a href="mailto:teacher@techtails.com" className="text-pinkprimary hover:text-orangeprimary font-medium">
              teacher@techtails.com
            </a>
          </li>
        </ul>
      </section>

      {/* ── ACKNOWLEDGMENT ── */}
      <section className="mb-10 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-3">Acknowledgment</h2>
        <p>
          By enrolling your child in any Tech Tails service you confirm that you have read,
          understood, and agree to this entire Agreement — including the No-Show Policy,
          Cancellation Policy, Code of Conduct, Liability Waiver, and all other sections above.
          This Agreement takes effect at enrollment and remains active for the duration of your
          Enrollee's participation.
        </p>
      </section>

      {/* ── CONTACT & PRIVACY ── */}
      <section className="mb-8">
        <p>
          Questions? Reach us at{" "}
          <a href="mailto:teacher@techtails.com" className="text-pinkprimary hover:text-orangeprimary font-medium">
            teacher@techtails.com
          </a>
          . For privacy details, see our{" "}
          <a
            href="/pages/privacypolicy"
            className="text-pinkprimary hover:text-orangeprimary font-medium"
          >
            Privacy Policy
          </a>
          .
        </p>
      </section>
    </div>
  );
};

export default TOS;
